import { useEffect, useRef } from 'react';

const HeroSection = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const particles: { x: number; y: number; vx: number; vy: number; alpha: number }[] = [];
    for (let i = 0; i < 80; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        alpha: Math.random() * 0.6 + 0.2,
      });
    }

    let animId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 217, 255, ${p.alpha})`;
        ctx.fill();
      });

      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(0, 217, 255, ${0.12 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(animate);
    };
    animate();

    const handleResize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-[#0A1628]">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/67cfeae23605705a5d94d64fee550cf2.jpg"
          alt="Hero background"
          className="w-full h-full object-cover object-top opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/90 via-[#0A1628]/70 to-[#0A1628]/50"></div>
      </div>

      {/* Particle canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pt-24 pb-16 flex flex-col lg:flex-row items-center gap-12">
        <div className="max-w-3xl lg:flex-1">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#00D9FF]/30 bg-[#00D9FF]/10 mb-8">
            <span className="w-2 h-2 rounded-full bg-[#00D9FF] animate-pulse block"></span>
            <span className="text-[#00D9FF] text-xs font-semibold tracking-widest uppercase">
              Live Platform — 2,847 Active Learners Now
            </span>
          </div>

          {/* Headline */}
          <h1 className="text-5xl md:text-7xl font-black text-white leading-tight mb-6">
            Master
            <br />
            <span className="text-[#00D9FF]">Cybersecurity</span>
            <br />
            Through Practice
          </h1>

          <p className="text-gray-300 text-lg md:text-xl leading-relaxed mb-10 max-w-xl">
            Join 50,000+ security professionals sharpening their skills daily through real-world labs, guided resources, and an expert community.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="#highlighted-labs"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#00D9FF] text-[#0A1628] font-bold text-base hover:bg-white transition-all duration-200 cursor-pointer whitespace-nowrap group"
            >
              Start Free Labs
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-200"></i>
            </a>
            <a
              href="#overview"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-white/30 text-white font-semibold text-base hover:border-[#00D9FF] hover:text-[#00D9FF] transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-circle-line"></i>
              Explore Community
            </a>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap gap-8 mt-14">
            {[
              { value: '500+', label: 'Security Labs' },
              { value: '50K+', label: 'Active Learners' },
              { value: '120+', label: 'Expert Mentors' },
              { value: '98%', label: 'Satisfaction Rate' },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-2xl font-black text-white">{stat.value}</div>
                <div className="text-gray-400 text-sm mt-0.5">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Hero image */}
        <div className="hidden lg:flex lg:flex-1 items-center justify-center">
          <div className="relative w-full max-w-lg">
            <div className="absolute inset-0 rounded-2xl bg-[#00D9FF]/10 blur-2xl scale-105"></div>
            <div className="relative rounded-2xl overflow-hidden border border-[#00D9FF]/20">
              <img
                src="https://storage.readdy-site.link/project_files/8d2afead-2811-4b57-a8ba-d5a8e0439fb7/1d0f2504-8e22-44e7-83ef-748768501ed5_Cyber-Practice-Field.png?v=50091916e038f6a60b372babe79692a6"
                alt="Cyber Practice Field"
                className="w-full h-auto object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/60 via-transparent to-transparent"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-gray-500 text-xs tracking-widest uppercase">Scroll</span>
        <i className="ri-arrow-down-line text-gray-500"></i>
      </div>
    </section>
  );
};

export default HeroSection;
